import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages, Parsers } from '../../gen-src/Localize';
import { DropdownArrowIcon, encodeSvg } from '../icons/Icons';
import { TabStyles } from '../styles/TabStyles';
import { DateTerminus, getDateFormatter, getDateWithTerminus, getParsedDate } from '../utils/Date';
import { makeTestId } from '../utils/Declarative';
import { findBlurRelatedTarget, generateId } from '../utils/DomUtil';
import { InputAttrs } from '../utils/HtmlAttrs';
import { HorizontalOriginOption, OriginOptions, Overlay, OverlayProps, VerticalOriginOption } from '../utils/Overlay';
import { assignRef, mergeEventHandlers } from '../utils/ReactHelpers';
import { ButtonProps } from './Button/ButtonWidget';
import { ButtonType, ButtonWidget } from './Button/ButtonWidgetShim';
import { DatePickerWidget } from './DatePicker/DatePickerWidget';
import { Img } from './DomWidgets';
import { TextFieldProps, TextFieldWidget } from './TextField/TextFieldWidget';

export enum DateFormat {
  ShortDateTime,
  MediumDateTime,
  LongDateTime,
  FullDateTime,
  ShortDate,
  MediumDate,
  LongDate,
  FullDate,
  ShortDateWithFullYear,
  ShortDateTimeWithFullYear,
  ShortDateTimeWithFullYear24Hour,
  ShortDateMediumTimeWithFullYear,
  ShortDateMediumTimeWithFullYear24Hour,
}

export enum DateInputFieldType {
  Line,
  Outline,
}

const { Shadow, Colors, Sizing } = TabStyles;

export interface DateInputFieldProps extends
  Pick<TextFieldProps, 'className' | 'label' | 'message' | 'required' | 'valid'>,
  Pick<OverlayProps, 'stackingRootId' | 'zIndex'>,
  Pick<React.AriaAttributes, 'aria-labelledby'> {

  /**
   * The input field styling.
   * @default ```DateInputFieldType.Line```
   */
  dateInputFieldType?: DateInputFieldType;
  disabled?: boolean;
  /**
   * The format the input text should be displayed in. If the text field has focus, it will not be reformatted until it blurs.
   * @default ```DateFormat.ShortDateWithFullYear```
   */
  displayFormat?: DateFormat;
  /**
   * Whether the input field has a date picker popup.
   * @default ```true```
   */
  hasDatePicker?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Only for styling the date picker popup. If defined, date that returns true will show a disabled style
   * and becomes unselectable.
   */
  isDatePickerDateDisabled?: (date: Date) => boolean;
  /**
   * Invoked when the user changes the text of the input field. This event is intended only for validation and fluid user experience,
   * e.g. to update the `valid`/`message` props if the date falls outside of an allowed range.
   * **DO NOT** update the `selectedDate` prop in response to the date in this event, that should be done in `onConfirm`.
   * @params `value` is a `Date` when the text parses as a valid date, and `null` otherwise.
   */
  onChange?: (value: Date | null) => void;
  /**
   * Invoked when a changed date is confirmed, i.e. when ENTER key is pressed, the picker closes, or the user blurs the field after
   * entering a valid date string. The `selectedDate` prop should be updated in response to this event in most cases.
   */
  onConfirm?: (value: Date) => void;
  placeholder?: string;
  /** The selected Date, if any. */
  selectedDate?: Date | null;
  /**
   * Whether the date picker popup displays an extra column showing the ISO Week numbers. This results in the displayed week
   * starting on Monday.
   */
  showDatePickerWeekNumbers?: boolean;
  /** Applied to the root element of this Widget. */
  style?: React.CSSProperties;
  /**
   * The way to handle undefined time components. If terminus is Start, all undefined time components will be 0,
   * if it is End, set all undefined time components to their respective max. For example, if the date "2/13/2015" is selected in start,
   * it implicitly refers to the earliest point within that day, "2/13/2015 00:00:00". When the exact same date is input into
   * through with terminus set to end, "2/13/2015" instead refers to "2/13/2015 23:59:59".
   * @default ```DateTerminus.Start```
   */
  terminus?: DateTerminus;
  testId?: string;
}

export interface DateInputFieldState {
  inputText: string | undefined;
  isDatePickerOpen: boolean;
  measuredLabelHeight: number;
}

const DropdownWrapperStyleName: string = TypeStyle.style({
  // maxWidth is the width of the picker, because input width cannot be wider than the picker.
  // probably a bug here if ISO week column is shown
  maxWidth: /*cellWidth*/ 30 * /*numCols*/ 7 + TabStyles.Spacing.DefaultSpace * 2 + TabStyles.Sizing.BorderWidth * 2,
  position: 'relative',
});
const ButtonDividerStyleName: string = TypeStyle.style({
  $nest: {
    '&::before': {
      background: Colors.L3,
      bottom: -1,
      content: `""`,
      display: 'block',
      pointerEvents: 'none',
      position: 'absolute',
      left: -1,
      top: -1,
      width: 1,
    },
    '&:active::before': { zIndex: -1 }
  }
});
const TextFieldContainerStyleName: string = TypeStyle.style({ display: 'flex' });
const TextFieldStyle: React.CSSProperties = {
  paddingRight: 24, // leaving space for dropdown button
  width: '100%',
};
const DialogStyle: React.CSSProperties = {
  boxShadow: Shadow.Standard,
  lineHeight: 'normal',
  outline: 'none',
};
const OverlayAnchorOrigin: OriginOptions = { vertical: VerticalOriginOption.bottom, horizontal: HorizontalOriginOption.left };

export class DateInputField extends React.Component<DateInputFieldProps & Omit<InputAttrs, keyof DateInputFieldProps | 'value'>, DateInputFieldState> {
  private inputElement: HTMLInputElement | null = null;
  private buttonElement: HTMLButtonElement | null = null;
  private overlayElement: HTMLElement | null = null;

  private readonly datePickerId: string = generateId();

  public static defaultProps: Pick<DateInputFieldProps, 'dateInputFieldType' | 'displayFormat' | 'hasDatePicker' | 'showDatePickerWeekNumbers'> = {
    dateInputFieldType: DateInputFieldType.Line,
    displayFormat: DateFormat.ShortDateWithFullYear,
    hasDatePicker: true,
    showDatePickerWeekNumbers: false,
  };

  public state: DateInputFieldState = {
    inputText: undefined,
    isDatePickerOpen: false,
    measuredLabelHeight: 20,
  };

  public componentDidMount(): void {
    window.setTimeout(this.setLabelHeight);
  }

  public componentDidUpdate(prevProps: DateInputFieldProps, prevState: DateInputFieldState): void {
    const { inputText, isDatePickerOpen } = this.state;
    const { displayFormat, selectedDate, valid } = this.props;

    if ((prevProps.valid !== false && valid === false) || (valid === false && this.props.message !== prevProps.message)) {
      // close the popup any time the error message is updated to ensure the user can see it (as it is not displayed in the popup)
      this.toggle(false);
    }

    if (prevState.isDatePickerOpen && !isDatePickerOpen && this.inputElement && document.body.contains(this.inputElement)) {
      this.inputElement.focus();
    }

    if (prevProps.displayFormat !== displayFormat || prevProps.selectedDate?.getTime() !== selectedDate?.getTime()) {
      this.setState({ inputText: undefined });
    } else if (inputText !== prevState.inputText) {
      const parsedDate = getParsedDate(inputText || '', this.props.terminus!);
      if (!parsedDate) {
        this.toggle(false); // if we can't parse it, we can't show it in the DatePicker popup
      }

      // inputText is `undefined` when the state is reset back to initial, but the empty string if the user deletes all the text
      // this is an important distinction when signaling the current state of the field to e.g. external validators
      this.props.onChange?.(inputText !== undefined ? parsedDate : (selectedDate || null));
    }

    if (prevProps.label !== this.props.label) {
      this.setLabelHeight();
    }
  }

  private setLabelHeight = () => {
    if (this.inputElement) {
      this.setState({ measuredLabelHeight: this.inputElement.parentElement!.offsetTop });
    }
  }

  private handleInputRef = (ref: HTMLInputElement | null): void => {
    this.inputElement = ref;
    assignRef(this.props.inputRef, ref);
  }

  private handleButtonRef = (ref: HTMLButtonElement | null): void => {
    this.buttonElement = ref;
  }

  private handleOverlayRef = (ref: HTMLElement | null): void => {
    this.overlayElement = ref;
  }

  /**
   * Toggles the dropdown popup.
   * @param open optional parameter, ```true``` to open the dropdown or ```false``` to close it.
   */
  public toggle(open: boolean = !this.state.isDatePickerOpen): void {
    if (!this.props.hasDatePicker || open === this.state.isDatePickerOpen) { return; }
    this.setState({ isDatePickerOpen: open });
  }

  private handleButtonClick = (): void => {
    this.toggle();
  }

  private handleInputChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const inputText = e.target.value;
    this.setState({ inputText });
  }

  private confirmIfChanged(date: Date): void {
    // TODO: ENTER or onBlur on an untouched field triggers a confirm event because the parser strips the time...bug in `getParsedDate`?
    if (date.getTime() !== this.props.selectedDate?.getTime()) {
      this.props.onConfirm?.(date);
    } else {
      this.closeAndClear();
    }
  }

  private confirmAndClose(date: Date): void {
    this.confirmIfChanged(date);
    this.toggle(false);
  }

  private handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = mergeEventHandlers(e => {
    if (e.key === 'Enter') {
      const parsedDate = getParsedDate((e.target as HTMLInputElement).value, this.props.terminus!);
      if (parsedDate) {
        this.confirmAndClose(parsedDate);
      }
    } else if (e.key === 'Escape') {
      this.closeAndClear();
    } else if (this.props.hasDatePicker) {
      switch (e.key) {
        case ' ':
          if (this.inputElement!.selectionStart === 0 && this.inputElement!.selectionEnd! >= this.inputElement!.value.length) {
            this.toggle(true);
            break;
          }
          return;
        case 'ArrowDown':
          this.toggle(true);
          break;
        case 'ArrowUp':
          this.toggle(false);
          break;
        default:
          return;
      }
    } else {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  }, e => this.props.onKeyDown?.(e));

  private handleOverlayKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Escape') {
      this.closeAndClear();
      e.preventDefault();
    }
    e.stopPropagation();
  }

  private handleBlur = (e: React.FocusEvent<HTMLElement>): void => {
    const toElement = findBlurRelatedTarget(e);
    if (this.buttonElement?.contains(toElement) || e.currentTarget.contains(toElement) || this.overlayElement?.contains(toElement)) {
      return;
    }

    const parsedDate = getParsedDate(this.inputElement!.value, this.props.terminus!);
    if (parsedDate && this.props.valid !== false) {
      this.confirmAndClose(parsedDate);
    } else {
      this.closeAndClear();
    }
  }

  private closeAndClear = (): void => {
    this.setState({ isDatePickerOpen: false, inputText: undefined });
  }

  private handlePickerChange = (date: Date): void => {
    const dateWithTerminus = getDateWithTerminus(date, Parsers.parseShortDate, this.props.terminus!);
    this.confirmIfChanged(dateWithTerminus);
  }

  private handlePickerConfirm = (date: Date): void => {
    this.setState({ inputText: undefined, isDatePickerOpen: false });
    this.handlePickerChange(date);
  }

  private getFormattedDate(date: DateInputFieldProps['selectedDate']): string {
    if (!date) { return ''; }
    return getDateFormatter(this.props.displayFormat!)(date);
  }

  private isOutlineTextField(): boolean {
    return this.props.dateInputFieldType === DateInputFieldType.Outline;
  }

  private renderTextField(): JSX.Element {
    // tslint:disable-next-line:deprecation max-line-length
    const { className, dateInputFieldType, displayFormat, hasDatePicker, inputRef, isDatePickerDateDisabled, message, onChange, onConfirm, selectedDate, showDatePickerWeekNumbers, stackingRootId, style, terminus, testId, valid, zIndex, ...textFieldProps } = this.props;
    const { inputText } = this.state;

    const isParsable: boolean = !inputText || !!getParsedDate(inputText, terminus!);

    return (
      <div onBlur={this.handleBlur} style={style} className={DropdownWrapperStyleName}>
        <TextFieldWidget
          {...(hasDatePicker && {
            'aria-expanded': this.state.isDatePickerOpen,
            'aria-haspopup': 'dialog',
            'aria-controls': (this.state.isDatePickerOpen ? this.datePickerId : undefined),
            role: 'combobox',
          })}
          {...textFieldProps}
          className={TextFieldContainerStyleName}
          kind={this.isOutlineTextField() ? 'outline' : 'line'}
          message={message || (!isParsable && Messages.enterValidDate())}
          onChange={this.handleInputChange}
          onKeyDown={this.handleInputKeyDown}
          ref={this.handleInputRef}
          style={TextFieldStyle}
          testId={makeTestId(testId!, 'text-field')}
          valid={isParsable ? valid : false}
          value={inputText ?? this.getFormattedDate(selectedDate)}
        />
        {hasDatePicker && this.renderDropdownButton()}
      </div>
    );
  }

  private renderDropdownButton(): React.ReactNode {
    const { BorderWidth, RowHeight } = Sizing;
    const isOutline = this.isOutlineTextField();
    const buttonStyle: React.CSSProperties = {
      borderRadius: 0,
      height: RowHeight - 2 * BorderWidth,
      minWidth: 0,
      position: 'absolute',
      right: BorderWidth,
      top: this.state.measuredLabelHeight + BorderWidth,
    };

    const buttonProps: ButtonProps = {
      buttonRef: this.handleButtonRef,
      buttonType: ButtonType.Icon,
      disableTabAccessibility: true,
      disabled: this.props.disabled,
      getContent: state => <Img draggable={false} src={encodeSvg(DropdownArrowIcon.icon(state))} />,
      handleClick: this.handleButtonClick,
      style: buttonStyle,
      testId: this.props.testId!,
    };
    return (
      <ButtonWidget {...buttonProps} className={TypeStyle.classes({ [ButtonDividerStyleName]: isOutline })} />
    );
  }

  private renderDatePicker = () => {
    return (
      <DatePickerWidget
        autofocus={true}
        isDateDisabled={this.props.isDatePickerDateDisabled}
        onChange={this.handlePickerChange}
        onConfirm={this.handlePickerConfirm}
        selectedDate={this.props.selectedDate || undefined}
        showWeekNumbers={this.props.showDatePickerWeekNumbers}
        testId={makeTestId(this.props.testId!, `date-picker`)}
      />
    );
  }

  private portalEventTrapOverrides: OverlayProps['portalEventTrapOverrides'] = {
    onBlur: this.handleBlur,
    onKeyUp: this.handleOverlayKeyUp
  };
  private renderOverlay(): React.ReactNode {
    const datePicker = this.props.hasDatePicker && this.state.isDatePickerOpen && this.renderDatePicker();
    if (!datePicker || !this.inputElement) { return null; }

    return (
      <Overlay
        allowAutoPosition={true}
        anchorEl={this.inputElement}
        anchorOrigin={OverlayAnchorOrigin}
        // tslint:disable-next-line:deprecation
        stackingRootId={this.props.stackingRootId}
        portalEventTrapOverrides={this.portalEventTrapOverrides}
        zIndex={this.props.zIndex}
      >
        <div
          id={this.datePickerId}
          ref={this.handleOverlayRef}
          role='dialog'
          style={DialogStyle}
          tabIndex={-1}
        >
          {datePicker}
        </div>
      </Overlay>
    );
  }

  public render(): JSX.Element {
    return (
      <div className={this.props.className} data-tb-test-id={this.props.testId}>
        {this.renderTextField()}
        {this.renderOverlay()}
      </div>
    );
  }
}
